import React, { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CloudArrowUpIcon, SpeakerWaveIcon, LanguageIcon } from '@heroicons/react/24/outline';
import { WizardStepper, WizardStep } from '@/components/Video/WizardStepper';
import { textToSpeechApi, translationApi } from '@/services/audioApi';
import BackgroundImagePicker from './BackgroundImagePicker';
import { CAPTION_FONT_OPTIONS, type AspectRatio } from '@/services/audioToVideoApi';
import LocalizedTextInput from '@/components/Common/LocalizedTextInput';
import LocalizedTextArea from '@/components/Common/LocalizedTextArea';

const STEPS: WizardStep[] = [
  { label: 'Audio', hint: 'Choose your speech' },
  { label: 'Background', hint: 'Pick your image' },
  { label: 'Captions', hint: 'Optional burned-in captions' },
  { label: 'Review', hint: 'Confirm & generate' },
];

const ASPECT_RATIOS: Array<{ value: AspectRatio; label: string }> = [
  { value: '16:9', label: '16:9 — Landscape (YouTube)' },
  { value: '9:16', label: '9:16 — Vertical (Shorts/Reels)' },
  { value: '1:1', label: '1:1 — Square' },
  { value: '4:5', label: '4:5 — Portrait' },
];

export interface CreateVideoWizardState {
  step: number;
  maxReached: number;
  title: string;
  titleOverlayEnabled: boolean;
  audioSourceType: 'ttsJob' | 'translationJob' | 'upload';
  selectedTtsJobId: string | null;
  selectedTranslationJobId: string | null;
  uploadedAudioFileId: string | null;
  uploadedAudioFileName: string | null;
  audioUploading: boolean;
  backgroundImageId: string | null;
  captionsEnabled: boolean;
  transcriptText: string;
  captionPosition: 'bottom' | 'top';
  captionFontFamily: string;
  aspectRatio: AspectRatio;
}

interface CreateVideoWizardProps {
  state: CreateVideoWizardState;
  setState: React.Dispatch<React.SetStateAction<CreateVideoWizardState>>;
  onUploadAudio: (file: File) => void;
  onGenerate: () => void;
  generating: boolean;
}

const CreateVideoWizard: React.FC<CreateVideoWizardProps> = ({ state, setState, onUploadAudio, onGenerate, generating }) => {
  const audioInputRef = useRef<HTMLInputElement>(null);

  const { data: ttsJobsData, isLoading: ttsJobsLoading } = useQuery({
    queryKey: ['tts-jobs-for-video'],
    queryFn: () => textToSpeechApi.getJobs({ status: 'completed', limit: 50 }),
  });
  const ttsJobs = (ttsJobsData?.data?.jobs ?? []).filter((j: any) => j.status === 'completed');

  const { data: translationJobsData, isLoading: translationJobsLoading } = useQuery({
    queryKey: ['translation-jobs-for-video'],
    queryFn: () => translationApi.getTranslationJobs({ status: 'completed', limit: 50 }),
  });
  const translationJobs = (translationJobsData?.data?.jobs ?? []).filter((j: any) => j.status === 'completed');

  const selectedTtsJob = ttsJobs.find((j: any) => j.jobId === state.selectedTtsJobId);
  const selectedTranslationJob = translationJobs.find((j: any) => j.jobId === state.selectedTranslationJobId);
  // The transcript is always in the selected source's own language — known
  // from the job itself, so the caption textarea gets correct direction/font
  // without the user having to tell it what language they're typing.
  const transcriptLanguage =
    state.audioSourceType === 'ttsJob'
      ? selectedTtsJob?.request?.language
      : state.audioSourceType === 'translationJob'
      ? selectedTranslationJob?.result?.targetLanguage
      : undefined;

  const goTo = (index: number) => setState((s) => ({ ...s, step: index, maxReached: Math.max(s.maxReached, index) }));
  const next = () => goTo(Math.min(STEPS.length - 1, state.step + 1));
  const back = () => goTo(Math.max(0, state.step - 1));

  const canProceedFromAudio =
    (state.audioSourceType === 'ttsJob' && !!state.selectedTtsJobId) ||
    (state.audioSourceType === 'translationJob' && !!state.selectedTranslationJobId) ||
    (state.audioSourceType === 'upload' && !!state.uploadedAudioFileId);
  const canProceedFromBackground = !!state.backgroundImageId;
  const canProceedFromCaptions = !state.captionsEnabled || state.transcriptText.trim().length > 0;

  return (
    <div className="space-y-6">
      <WizardStepper steps={STEPS} current={state.step} maxReached={state.maxReached} onJump={goTo} />

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        {state.step === 0 && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setState((s) => ({ ...s, audioSourceType: 'ttsJob' }))}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium border ${
                  state.audioSourceType === 'ttsJob'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                }`}
              >
                From Text-to-Speech
              </button>
              <button
                type="button"
                onClick={() => setState((s) => ({ ...s, audioSourceType: 'translationJob' }))}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium border ${
                  state.audioSourceType === 'translationJob'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                }`}
              >
                From Audio Translation
              </button>
              <button
                type="button"
                onClick={() => setState((s) => ({ ...s, audioSourceType: 'upload' }))}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium border ${
                  state.audioSourceType === 'upload'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                }`}
              >
                Upload audio file
              </button>
            </div>

            {state.audioSourceType === 'ttsJob' ? (
              ttsJobsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : ttsJobs.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                  No completed text-to-speech jobs yet — generate one on the Text to Speech page first, or upload an audio file instead.
                </p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {ttsJobs.map((job: any) => (
                    <button
                      key={job.jobId}
                      type="button"
                      onClick={() => setState((s) => ({ ...s, selectedTtsJobId: job.jobId }))}
                      className={`w-full text-left px-3 py-2 rounded-md border flex items-center gap-2 ${
                        state.selectedTtsJobId === job.jobId
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <SpeakerWaveIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                      <span className="text-sm text-gray-900 dark:text-white truncate">
                        {job.title || (job.request?.text ? job.request.text.slice(0, 60) : `TTS Job ${job.jobId.slice(0, 8)}`)}
                      </span>
                      {job.result?.duration && (
                        <span className="ml-auto text-xs text-gray-400 flex-shrink-0">{Math.round(job.result.duration)}s</span>
                      )}
                    </button>
                  ))}
                </div>
              )
            ) : state.audioSourceType === 'translationJob' ? (
              translationJobsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : translationJobs.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                  No completed audio translation jobs yet — run one on the Audio Translation page first, or upload an audio file instead.
                </p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {translationJobs.map((job: any) => (
                    <button
                      key={job.jobId}
                      type="button"
                      onClick={() => setState((s) => ({ ...s, selectedTranslationJobId: job.jobId }))}
                      className={`w-full text-left px-3 py-2 rounded-md border flex items-center gap-2 ${
                        state.selectedTranslationJobId === job.jobId
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <LanguageIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                      <span className="text-sm text-gray-900 dark:text-white truncate">
                        {job.result?.targetLanguage
                          ? `${(job.result.translatedText || '').slice(0, 50) || `Translation ${job.jobId.slice(0, 8)}`} (${job.result.targetLanguage.toUpperCase()})`
                          : `Translation Job ${job.jobId.slice(0, 8)}`}
                      </span>
                    </button>
                  ))}
                </div>
              )
            ) : (
              <div>
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setState((s) => ({ ...s, uploadedAudioFileName: file.name }));
                      onUploadAudio(file);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => audioInputRef.current?.click()}
                  disabled={state.audioUploading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-blue-400 disabled:opacity-50"
                >
                  <CloudArrowUpIcon className="h-5 w-5" />
                  {state.audioUploading
                    ? 'Uploading…'
                    : state.uploadedAudioFileId
                    ? `Uploaded: ${state.uploadedAudioFileName}`
                    : 'Click to select an audio file'}
                </button>
              </div>
            )}
          </div>
        )}

        {state.step === 1 && (
          <BackgroundImagePicker
            value={state.backgroundImageId}
            onChange={(id) => setState((s) => ({ ...s, backgroundImageId: id }))}
          />
        )}

        {state.step === 2 && (
          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={state.captionsEnabled}
                onChange={(e) => setState((s) => ({ ...s, captionsEnabled: e.target.checked }))}
              />
              <span className="text-sm font-medium text-gray-900 dark:text-white">Burn in captions</span>
            </label>

            {state.captionsEnabled && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Transcript</label>
                  <LocalizedTextArea
                    language={transcriptLanguage}
                    value={state.transcriptText}
                    onChange={(e) => setState((s) => ({ ...s, transcriptText: e.target.value }))}
                    rows={6}
                    placeholder={state.audioSourceType === 'ttsJob' ? 'Pre-filled from the selected speech text — edit if needed' : 'Enter the transcript text to caption'}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
                  />
                </div>
                <div className="flex gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Position</label>
                    <select
                      value={state.captionPosition}
                      onChange={(e) => setState((s) => ({ ...s, captionPosition: e.target.value as 'bottom' | 'top' }))}
                      className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
                    >
                      <option value="bottom">Bottom</option>
                      <option value="top">Top</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Font</label>
                    <select
                      value={state.captionFontFamily}
                      onChange={(e) => setState((s) => ({ ...s, captionFontFamily: e.target.value }))}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
                    >
                      {CAPTION_FONT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                      If captions show the wrong glyphs for your language, pick the matching script explicitly instead of auto-detect.
                    </p>
                  </div>
                </div>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Captions are timed by evenly distributing the transcript across the audio's duration, not real speech
                  alignment — timing may drift from the spoken pace on longer clips.
                </p>
              </>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Aspect ratio</label>
              <div className="grid grid-cols-2 gap-2">
                {ASPECT_RATIOS.map((ar) => (
                  <button
                    key={ar.value}
                    type="button"
                    onClick={() => setState((s) => ({ ...s, aspectRatio: ar.value }))}
                    className={`px-3 py-2 rounded-md text-sm border text-left ${
                      state.aspectRatio === ar.value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {ar.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {state.step === 3 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Review</h3>
            <dl className="text-sm space-y-1.5">
              <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">Audio source</dt><dd className="text-gray-900 dark:text-white">{
                state.audioSourceType === 'ttsJob' ? (selectedTtsJob?.title || 'Text-to-Speech job') :
                state.audioSourceType === 'translationJob' ? (selectedTranslationJob ? `Audio translation (${selectedTranslationJob.result?.targetLanguage?.toUpperCase() || ''})` : 'Audio translation job') :
                state.uploadedAudioFileName
              }</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">Captions</dt><dd className="text-gray-900 dark:text-white">{
                state.captionsEnabled
                  ? `On (${state.captionPosition}, ${CAPTION_FONT_OPTIONS.find((o) => o.value === state.captionFontFamily)?.label.split(' —')[0] || state.captionFontFamily})`
                  : 'Off'
              }</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">Aspect ratio</dt><dd className="text-gray-900 dark:text-white">{state.aspectRatio}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">Title overlay</dt><dd className="text-gray-900 dark:text-white">{state.titleOverlayEnabled && state.title.trim() ? 'On' : 'Off'}</dd></div>
            </dl>
            <LocalizedTextInput
              value={state.title}
              onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
              placeholder="Video title (optional)"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
            />
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={state.titleOverlayEnabled}
                onChange={(e) => setState((s) => ({ ...s, titleOverlayEnabled: e.target.checked }))}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Embed title in video (persistent banner across the top)
              </span>
            </label>
            {state.titleOverlayEnabled && !state.title.trim() && (
              <p className="text-xs text-amber-600 dark:text-amber-400">Enter a title above for this to take effect.</p>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={back}
          disabled={state.step === 0}
          className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 disabled:opacity-40"
        >
          Back
        </button>
        {state.step < STEPS.length - 1 ? (
          <button
            type="button"
            onClick={next}
            disabled={
              (state.step === 0 && !canProceedFromAudio) ||
              (state.step === 1 && !canProceedFromBackground) ||
              (state.step === 2 && !canProceedFromCaptions)
            }
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            onClick={onGenerate}
            disabled={generating}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
          >
            {generating ? 'Generating…' : 'Generate Video'}
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateVideoWizard;
